import { cn } from "@/lib/utils";

/**
 * Types and small pieces shared across the administration panels.
 *
 * ⚠️ The sensitivity levels are declared here in the order they should be OFFERED, which is
 * not an order of severity. Clearance is a set, never a ladder — Clinical does not imply
 * Sensitive and Sensitive does not imply Clinical — so a control that presented them as a
 * slider or a "minimum level" would misrepresent the model badly enough to cause a real
 * misconfiguration. Every clearance control in this app is therefore a checkbox group.
 */

export const SENSITIVITY_LEVELS = ["Standard", "Sensitive", "Clinical", "Restricted"] as const;

export type SensitivityLevel = (typeof SENSITIVITY_LEVELS)[number];

/** What each classification is for, shown wherever one is being chosen. */
export const LEVEL_DESCRIPTIONS: Record<SensitivityLevel, string> = {
  Standard: "General operational. Nothing personnel- or patient-related.",
  Sensitive: "Personnel, compensation and employment matters.",
  Clinical: "Clinical systems, patient safety, anything patient-linked.",
  Restricted:
    "Personnel matters whose subject may be on the owning team — harassment complaints, corrective action, investigations.",
};

export interface AdminRole {
  id: string;
  name: string;
  description: string;
  isSystem: boolean;
  permissions: string[];
  grants: { permission: string; scope: string }[];
  clearedLevels: string[];
  globalAssignmentCount: number;
  teamMembershipCount: number;
  createdAtUtc: string;
}

export interface AdminTeam {
  id: string;
  name: string;
  description: string;
  isActive: boolean;
  clearedLevels: string[];

  /**
   * Counts from `GET /api/admin/teams`, which is gated on `team.manage` rather than
   * `ticket.read` — every requester needs a team NAME to raise a request, nobody outside
   * administration needs its headcount.
   *
   * They are on the card because they are what decides whether an action will be refused:
   * a team with members or open requests cannot be deleted, and one with open requests
   * cannot be closed.
   */
  memberCount: number;
  categoryCount: number;
  openTicketCount: number;
}

export interface AdminCategory {
  id: string;
  teamId: string;
  name: string;
  sensitivity: string;
  isActive: boolean;
}

export interface CatalogPermission {
  permission: string;
  group: string;
  displayName: string;
  description: string;
  /**
   * ⚠️ `Capability` is scope-agnostic like `Administrative` — the endpoint check reads no
   * scope, so it is offered at Global only — but holding one does NOT make somebody an
   * administrator (D-67). Never branch on this to decide whether to show an administrative
   * affordance: `IsAdministrator` on `GET /api/me` is the single definition (D-36, D-39), and
   * recomputing it in the browser from a permission list is the thing that rule forbids.
   */
  kind: "Record" | "Administrative" | "Capability";
  isHighPrivilege: boolean;
  scopes: string[];
  heldByRoles: string[];
}

/**
 * Standard is the everyday level; every other one widens what a holder can read, so they
 * are coloured as a warning rather than as decoration.
 */
export function LevelChip({ level, className }: { level: string; className?: string }) {
  return (
    <span
      className={cn(
        "rounded-full px-2 py-0.5 text-[11px] font-medium",
        level === "Standard"
          ? "bg-secondary text-secondary-foreground"
          : level === "Restricted"
            ? "bg-destructive/15 text-destructive"
            : "bg-warning/15 text-warning",
        className,
      )}
    >
      {level}
    </span>
  );
}

/** The dotted permission name, rendered so it reads as an identifier rather than prose. */
export function PermissionChip({ name }: { name: string }) {
  return (
    <span className="rounded bg-secondary px-1.5 py-0.5 font-mono text-[10px] text-secondary-foreground">
      {name}
    </span>
  );
}

export function ErrorNote({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm"
    >
      {message}
    </div>
  );
}

export function PanelSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-2" aria-busy="true">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="h-20 animate-pulse rounded-xl border border-border bg-card" />
      ))}
    </div>
  );
}
